import { motion } from 'motion/react';
import { ImageWrapper } from './ImageWrapper.jsx';
import { getProductInfo } from '../utils/productHelpers.js';
import ElectricBorder from './ElectricBorder.jsx';
import locationIcon from '../images/location.svg';
import Currency from '../utils/Currency.js';

export function ProductCard({ currency, locale, from, query, product, sellers, sellerColors, onProductClick, style, filteredStoreIds, disableFeaturedBorder, logActivity }) {
  const { commonPrice, normalPrice, mainImage } = getProductInfo(product, filteredStoreIds);
  const rootElement = document.documentElement;
  const computedStyles = getComputedStyle(rootElement);
  const featuredBorderColor = style?.global?.products?.featuredBorderColor || computedStyles.getPropertyValue('--product-border');
  const featuredBorderSpeed = style?.global?.products?.featuredBorderSpeed || 0;
  const featuredBorderChaos = style?.global?.products?.featuredBorderChaos || 0;
  const featuredBorderThickness = style?.global?.products?.featuredBorderThickness || 2;
  const featuredBorderLabel = style?.global?.products?.featuredLabel;
  const isFeatured = product.is_featured;

  function darkenHexColor(hex, percent) {
    hex = hex.replace(/^#/, '');
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);
    const factor = (100 - percent) / 100;
    r = Math.floor(r * factor);
    g = Math.floor(g * factor);
    b = Math.floor(b * factor);
    r = Math.max(0, r);
    g = Math.max(0, g);
    b = Math.max(0, b);
    const toHex = (value) => {
      const hexStr = value.toString(16);
      return hexStr.length === 1 ? '0' + hexStr : hexStr;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  function lightenHexColor(hex, percent) {
    hex = hex.replace(/^#/, '');
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);
    const factor = percent / 100;
    r = Math.min(255, Math.floor(r + (255 - r) * factor));
    g = Math.min(255, Math.floor(g + (255 - g) * factor));
    b = Math.min(255, Math.floor(b + (255 - b) * factor));
    const toHex = (value) => {
      const hexStr = value.toString(16);
      return hexStr.length === 1 ? '0' + hexStr : hexStr;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  const getSellerIndex = (sellerName) => {
    let index = sellers.findIndex(s => s === sellerName);
    if (index > sellerColors.length - 1) return index % sellerColors.length;
    return index !== -1 ? index : 0;
  };

  const handleClick = (product) => {
    document.activeElement?.blur();
    logActivity('product-card', 'product-click', 'click', true, {product_id: product.id, product_name: product.label || product.name || null, from: from || 'unknown', query: query || null});
    onProductClick(product);
  };

  let width = "w-auto";
  const sellerIndex = getSellerIndex(product.seller_name);
  const sellerColor = sellerColors[sellerIndex];
  let featuredLabelColor = darkenHexColor(featuredBorderColor, 50);
  if (featuredLabelColor === featuredBorderColor) featuredLabelColor = lightenHexColor(featuredBorderColor, 80);

  const getCardContent = () => (
    <motion.div
      onClick={() => handleClick(product)}
      className={`item-card rounded-[30px] ${width} max-w-[100%] p-5 flex flex-col gap-5 items-center cursor-pointer${isFeatured ? ' border-transparent!' : ' shadow-sm hover:shadow-md transition-shadow'}`}
    >
      <div className="w-full aspect-square rounded-[30px] overflow-hidden relative">
        <ImageWrapper
          image={mainImage}
          alt={product.label || product.name}
          display="block"
          width="100%"
          height="100%"
          backgroundColor="transparent"
          margin="0"
          padding="0"
          borderRadius="30px"
          transition={true}
          loadDelay={0.1}
          onLoad={undefined}
        />
        {product.seller_name ? (
          <div
            className="secondary-font-bold fs-16 px-5 py-2 w-full absolute bottom-0 left-0 flex items-center gap-2"
            style={{color: '#FFFFFF', background: sellerColor, textShadow: '#000000 2px 2px 2px'}}
          >
            <ImageWrapper
              image={locationIcon}
              alt="Location"
              display="block"
              width="24px"
              height="24px"
              backgroundColor="transparent"
              margin="0"
              padding="0"
              borderRadius="0"
              transition={true}
              loadDelay={0.1}
              onLoad={undefined}
            />
            <div className="overflow-hidden text-ellipsis line-clamp-1 pr-[3px]">
              {product.seller_name}
            </div>
          </div>
          ) : null}
      </div>
      <div className="w-full flex flex-col gap-3">
        <div className="px-0">
          <p className='line-clamp-2 mb-2 leading-tight uppercase tracking-[0.5px] h-[calc(40px*var(--fs-scale))] secondary-font-bold'>
            {product.label || product.name}
          </p>
          <p className="secondary-font-regular text-sm mb-2 line-clamp-2 h-[calc(40px*var(--fs-scale))] text-muted">
            {product.description}
          </p>
          <div className='show-on-debug'>
            <ul>
              <li>
                id: {product.id}
              </li>
              <li>
                sku: {product.sku}
              </li>
              {product.ean ? <li>
                ean: {product.ean}
              </li> : null}
            </ul>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-3xl font-bold text-common-price overflow-hidden text-ellipsis">
            {(commonPrice) ? new Currency(currency, locale).format(commonPrice) : "No disponible"}
          </span>
          {(commonPrice && normalPrice && commonPrice < normalPrice) ? <span className="text-sm text-old-price">{normalPrice ? new Currency(currency, locale).format(normalPrice) : "No disponible"}</span> : ""}
        </div>
      </div>
    </motion.div>
  );

  if (isFeatured && !disableFeaturedBorder) {
    return (
      <motion.div
        className={`relative ${width} w-full`}
        whileHover={{ y: -10 }}
        whileTap={{ scale: 0.9 }}
      >
        <div className={`relative ${width} mx-auto`}>
          {featuredBorderLabel ? (
            <div className='w-full flex justify-center absolute top-[-30px] z-[3]'>
              <div className='secondary-font-bold fs-16 px-5 py-2 w-max rounded-[10px] uppercase' style={{color: featuredLabelColor, backgroundColor: featuredBorderColor}}>
                {featuredBorderLabel}
              </div>
            </div>
          ) : null}
          <ElectricBorder
            color={featuredBorderColor}
            speed={featuredBorderSpeed}
            chaos={featuredBorderChaos}
            thickness={featuredBorderThickness}
            style={{ borderRadius: 30 }}
          >
            {getCardContent()}  
          </ElectricBorder>
        </div>
      </motion.div>
    );
  } else {
    return (
      <motion.div
        className="w-full"
        whileHover={{ y: -10 }}
        whileTap={{ scale: 0.5 }}
      >
        <div className={`relative ${width} mx-auto`}>
          {getCardContent()}
        </div>
      </motion.div>
    );
  }
}
